<script setup>
import { ref, computed, getCurrentInstance } from 'vue'
import { router } from '@inertiajs/vue3';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';
import { useToast } from 'primevue/usetoast';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';

defineOptions({
    layout: AdminMainLayout,
});

const { proxy } = getCurrentInstance();
const toast = useToast();
const { showLoading, hideLoading } = useLoading();
const { formatConvertedPrice, getCurrentCurrency, onCurrencyChange } = useCurrencyFormatter();
const currentCurrency = ref(getCurrentCurrency());

onCurrencyChange(({ currency }) => {
    currentCurrency.value = currency;
});

const props = defineProps({
    budgets: Object,
});

const deleteDialogVisible = ref(false);
const budgetToDelete = ref(null);

const formatCurrencyConverted = (value, coin = 'USD') => {
    if (!value && value !== 0) return '-';
    return formatConvertedPrice(value, coin);
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR');
};

const previewBudget = (budget) => {
    const url = proxy.route ? proxy.route('budgets.preview', budget.budget_number) : `/admin/budgets/${budget.budget_number}/preview`;
    window.open(url, '_blank');
};

const editBudget = (budget) => {
    const routeName = proxy.route ? proxy.route('budgets.edit', budget.budget_number) : `/admin/budgets/${budget.budget_number}/edit`;
    router.visit(routeName, {
        onStart: () => showLoading(proxy.$t('Loading budget...')),
        onFinish: () => hideLoading(),
    });
};

const importToOrder = (budget) => {
    const baseUrl = proxy.route ? proxy.route('orders.create') : '/admin/orders/create';
    const url = `${baseUrl}?editBudgetNumber=${budget.budget_number}&mode=order`;
    router.visit(url, {
        onStart: () => showLoading(proxy.$t('Importing to order...')),
        onFinish: () => hideLoading(),
    });
};

const downloadBudget = (budget) => {
    const url = proxy.route ? proxy.route('budgets.download', budget.budget_number) : `/admin/budgets/${budget.budget_number}/download`;
    window.open(url, '_blank');
};

const confirmDeleteBudget = (budget) => {
    budgetToDelete.value = budget;
    deleteDialogVisible.value = true;
};

const deleteBudget = () => {
    const budget = budgetToDelete.value;
    if (!budget) return;

    deleteDialogVisible.value = false;

    const routeName = proxy.route ? proxy.route('budgets.destroy', budget.budget_number) : `/admin/budgets/${budget.budget_number}`;
    router.delete(routeName, {
        preserveScroll: true,
        onStart: () => showLoading(proxy.$t('Deleting budget...')),
        onSuccess: () => {
            hideLoading();
            toast.add({ severity: 'success', summary: proxy.$t('Success'), detail: proxy.$t('Budget deleted successfully.'), life: 3000 });
        },
        onError: () => {
            hideLoading();
            toast.add({ severity: 'error', summary: proxy.$t('Error'), detail: proxy.$t('Failed to delete budget.'), life: 3000 });
        },
        onFinish: () => {
            hideLoading();
            budgetToDelete.value = null;
        },
    });
};

const links = computed(() => {
    if (!props.budgets?.links) return [];
    const _links = [...props.budgets.links];
    _links.shift();
    _links.pop();
    return _links;
});
</script>

<template>
    <div class="card">
        <div class="flex items-center justify-between mb-6">
            <h2 class="text-2xl font-bold m-0">{{ $t('Budgets') }}</h2>
            <Button
                :label="$t('New Budget')"
                icon="pi pi-plus"
                class="p-button-primary"
                @click="() => {
                    const url = proxy.route ? proxy.route('budgets.create') : '/admin/budgets/create';
                    router.visit(url, { onStart: () => showLoading(proxy.$t('Loading...')), onFinish: () => hideLoading() });
                }"
            />
        </div>

        <DataTable
            :value="budgets.data"
            :rows="15"
            dataKey="budget_number"
            responsiveLayout="scroll"
            class="p-datatable-sm budgets-table"
            stripedRows
        >
            <template #empty>
                <div class="text-center py-8 text-gray-500">
                    <i class="pi pi-file-edit text-4xl mb-3 block"></i>
                    <p>{{ $t('No budgets found.') }}</p>
                </div>
            </template>

            <Column field="budget_number" header="#" :sortable="false" style="width: 150px">
                <template #body="{ data }">
                    <div class="flex items-center gap-2">
                        <span class="font-semibold whitespace-nowrap">{{ data.budget_number }}</span>
                        <Tag
                            v-if="data.has_price_changes"
                            severity="warn"
                            v-tooltip.top="$t('One or more sites have updated prices since this budget was created.')"
                            class="text-xs cursor-pointer"
                        >
                            <i class="pi pi-exclamation-triangle mr-1" style="font-size:0.65rem"></i>
                            {{ $t('Outdated prices') }}
                        </Tag>
                    </div>
                </template>
            </Column>

            <Column field="created_at" :header="$t('Date')" :sortable="false" style="width: 130px">
                <template #body="{ data }">
                    {{ formatDate(data.created_at) }}
                </template>
            </Column>

            <Column :header="$t('# Sites')" :sortable="false" style="width: 100px">
                <template #body="{ data }">
                    <Tag :value="data.sites_count + ' sites'" severity="info" />
                </template>
            </Column>

            <Column :header="$t('Sites')" :sortable="false">
                <template #body="{ data }">
                    <div class="flex flex-wrap gap-1">
                        <span
                            v-for="item in (data.items || []).slice(0, 5)"
                            :key="item.id"
                            class="text-xs bg-gray-100 rounded px-2 py-1"
                        >
                            {{ item.site?.url?.replace(/^https?:\/\//, '') || '-' }}
                        </span>
                        <span v-if="(data.items || []).length > 5" class="text-xs text-gray-500">
                            +{{ (data.items || []).length - 5 }} more
                        </span>
                    </div>
                </template>
            </Column>

            <Column :header="$t('Total (Cost)')" :sortable="false" style="width: 150px">
                <template #body="{ data }">
                    <span class="font-semibold text-orange-600">{{ formatCurrencyConverted(data.total_cost, data.cost_coin) }}</span>
                </template>
            </Column>

            <Column :header="$t('Total (Sale)')" :sortable="false" style="width: 150px">
                <template #body="{ data }">
                    <span class="font-semibold text-green-600">{{ formatCurrencyConverted(data.total_sale, data.sale_coin) }}</span>
                </template>
            </Column>

            <Column :header="$t('Actions')" :sortable="false" style="width: 260px">
                <template #body="{ data }">
                    <div class="flex gap-2">
                        <Button
                            icon="pi pi-eye"
                            class="p-button-sm p-button-text p-button-secondary"
                            v-tooltip.top="$t('Preview PDF')"
                            @click="previewBudget(data)"
                        />
                        <Button
                            icon="pi pi-pencil"
                            class="p-button-sm p-button-text p-button-info"
                            v-tooltip.top="$t('Edit')"
                            @click="editBudget(data)"
                        />
                        <Button
                            icon="pi pi-shopping-cart"
                            class="p-button-sm p-button-text p-button-warning"
                            v-tooltip.top="$t('Import to Order')"
                            @click="importToOrder(data)"
                        />
                        <Button
                            icon="pi pi-download"
                            class="p-button-sm p-button-text p-button-success"
                            v-tooltip.top="$t('Download PDF')"
                            @click="downloadBudget(data)"
                        />
                        <Button
                            icon="pi pi-trash"
                            class="p-button-sm p-button-text p-button-danger"
                            v-tooltip.top="$t('Delete')"
                            @click="confirmDeleteBudget(data)"
                        />
                    </div>
                </template>
            </Column>
        </DataTable>

        <!-- Pagination -->
        <div v-if="budgets.last_page > 1" class="flex items-center justify-between mt-4">
            <span class="text-sm text-gray-600">
                {{ $t('Showing') }} {{ budgets.from }} {{ $t('to') }} {{ budgets.to }} {{ $t('of') }} {{ budgets.total }} {{ $t('budgets') }}
            </span>
            <div class="flex gap-1">
                <Button
                    v-for="link in links"
                    :key="link.label"
                    :label="link.label"
                    :class="['p-button-sm', link.active ? 'p-button-primary' : 'p-button-text']"
                    :disabled="!link.url"
                    @click="link.url && router.visit(link.url, { preserveScroll: true })"
                />
            </div>
        </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <Dialog v-model:visible="deleteDialogVisible" :header="$t('Confirm Deletion')" :modal="true" :style="{ width: '450px' }" :closable="true">
        <div class="flex items-center gap-3">
            <i class="pi pi-exclamation-triangle text-3xl text-orange-500"></i>
            <span>{{ $t('Are you sure you want to delete this budget?') }}</span>
        </div>

        <template #footer>
            <Button :label="$t('Cancel')" icon="pi pi-times" class="p-button-text" @click="deleteDialogVisible = false" />
            <Button :label="$t('Delete')" icon="pi pi-trash" class="p-button-danger" @click="deleteBudget" />
        </template>
    </Dialog>

</template>

<style scoped>
/* Budgets table: header titles must always stay on a single line */
.budgets-table :deep(.p-datatable-column-title) {
    white-space: nowrap;
}

.budgets-table :deep(.p-datatable-header-cell) {
    white-space: nowrap;
}
</style>
